import { useEffect, useState } from 'react'
import {
  RainfallChart,
  VegetronixVH400Chart,
  WindDirectionChart,
  GenericLineChart,
  FlowMeterChart,
  UnifiedChartVegetronixVH400,
  UnifiedChartIrrometer,
  VegetronixTHERM200Chart,
  UnifiedChartVegetronixTHERM200,
  BatteryLineChart,
} from '../components/charts'
import { useAccountContext, useAppContext } from '../contexts'
import { DeviceAlertSettings, DeviceDetails, DeviceSiteDetails } from '../components'
import { Spinner } from '../components/Spinner'
import {
  AlertSettingsItem,
  AlertTypeEnums,
  ALERT_SETTINGS_GLOBAL_DEVICE_ID,
  SCHD_INTERVAL_MINUTES,
} from '../common/constants'
import { getAlertSettingsFromBackend } from '../services/alertSettings'
import { api } from '../services'
import { SensorProduct, UnitCategoryId } from '../common/enums'
import { DeviceSensorSettings } from '../components/DeviceSensorSettings'
import { BadgerSDIChart } from '../components/charts/BadgerSDIChart'
import { Viewport } from '../common/types'
// import { UnifiedChartVegetronixVH400 } from '../components/charts/UnifiedChartVegetronixVH400'
// import { UnifiedChartIrrometer } from '../components/charts/UnifiedChartIrrometer'
// import { VegetronixTHERM200Chart } from '../components/charts/VegetronixTHERM200Chart'

enum RicMode {
  Standard = 'Standard',
  WeatherStation = 'WeatherStation',
  WaterQuality = 'WaterQuality',
  DirtTracker = 'DirtTracker',
  FlowMeter = 'FlowMeter',
  Unknown = 'Unknown',
}

export const RicModeLookup: Record<number, RicMode> = {
  0: RicMode.Standard,
  1: RicMode.WeatherStation,
  2: RicMode.WaterQuality,
  3: RicMode.DirtTracker,
  1000: RicMode.FlowMeter, // there is official value for FlowMeter so we use 1000
  5000: RicMode.Unknown,
}

interface Props {
  sigfoxId: string
  viewport: Viewport
}

export function ChartsPage({ sigfoxId, viewport }: Props) {
  const { currentDate, showBatteryCharts } = useAppContext()
  const {
    devices,
    userUnitSettings,
    unitConversions,
    selectedDevice,
    // getDevice
  } = useAccountContext()

  const [isLoading, setIsLoading] = useState(false)
  const [readings, setReadings] = useState()
  const [batteryReadings, setBatteryReadings] = useState()
  const [sensorConfigs, setSensorConfigs] = useState<Record<string, any>>({})

  const [globalAlertSettings, setGlobalAlertSettings] = useState<AlertSettingsItem[]>([])
  const [currentTime, setCurrentTime] = useState(new Date().getTime())
  const [deviceConfig, setDeviceConfig] = useState<any>()

  const device = devices[sigfoxId]

  const refreshTrigger = currentDate

  useEffect(() => {
    getAlertSettingsFromBackend(ALERT_SETTINGS_GLOBAL_DEVICE_ID, AlertTypeEnums.SENSOR_ALERTS)
      .then((settings) => setGlobalAlertSettings(settings))
      .catch((error) => console.error(error))
    return () => {}
  }, [])

  useEffect(() => {
    api
      .getDeviceConfig(sigfoxId)
      .then((response) => {
        setDeviceConfig(response.config)
      })
      .catch((error) => console.error(error))
  }, [sigfoxId])

  useEffect(() => {
    if (isLoading) return
    setSensorConfigs({})
    setIsLoading(true)
    setCurrentTime(new Date().getTime())
    const from = Math.round(viewport.minimum / 1000) // get from 0 (the earliest time possible in order to render all charts)
    const to = Math.round(viewport.maximum / 1000)

    const totalDays = (to - from) / (60 * 60 * 24)
    const numberOfDataPoints = totalDays * 24 * (60 / SCHD_INTERVAL_MINUTES)
    const limit = numberOfDataPoints * 2

    getData()

    async function getData() {
      try {
        const [readings, sensorConfig, batteryReadings] = await Promise.all([
          api.getReadings(sigfoxId, from, to, limit),
          api.getSensorConfigs(sigfoxId, to),
          api.getBatteryReadings(sigfoxId, from, to),
        ])

        setReadings(readings.data)
        setSensorConfigs(sensorConfig.data)
        setBatteryReadings(batteryReadings.data)
      } catch (error) {
        console.error(error)
      } finally {
        setIsLoading(false)
      }
    }

    return () => {
      setIsLoading(false)
    }
  }, [refreshTrigger, selectedDevice, viewport]) // eslint-disable-line react-hooks/exhaustive-deps

  function renderAllCharts(readings: any): JSX.Element[] {
    if (isLoading) return []

    const charts: JSX.Element[] = []

    if (
      sensorConfigs === undefined ||
      readings === undefined ||
      Object.keys(unitConversions).length === 0 ||
      Object.keys(userUnitSettings).length === 0
    ) {
      return []
    }

    if (readings && Object.keys(readings).length === 0) {
      return [<div className={tailwind.alert}>No data available this time period</div>]
    }

    const mode = determineRicMode(deviceConfig, readings)

    const data: Record<string, any> = {}
    let order: any[] = []

    if (mode === RicMode.FlowMeter) {
      Object.keys(readings).forEach((key: any) => {
        const [sensorPort] = key.split('-')

        if (data[sensorPort] === undefined) {
          data[sensorPort] = []
          order.push(sensorPort)
        }

        data[sensorPort].push(readings[key])
      })

      // filter out temperature sensor
      order = order.filter((item) => item !== 'T0')

      order.forEach((sensorPort) => {
        const unitCategoryId = data[sensorPort][0].metadata.units.category.id
        const chart: JSX.Element | null | undefined =
          data[sensorPort].length > 1
            ? renderUnifiedCharts(data[sensorPort])
            : renderIndividualChart(unitCategoryId, data[sensorPort][0])

        if (chart) {
          charts.push(chart)
        }
      })
    } else {
      Object.keys(readings).forEach((key: any) => {
        const [sensorPort, unitCategoryId] = key.split('-')

        const metadata = readings[key].metadata
        if (metadata.productName !== 'none') {
          if (data[unitCategoryId] === undefined) {
            data[unitCategoryId] = []
            if (mode !== RicMode.WeatherStation && sensorPort !== 'T0') {
              order.push(Number(unitCategoryId))
            }
          }
          data[unitCategoryId].push(readings[key])
        }
      })

      switch (mode) {
        case RicMode.WeatherStation:
          data[UnitCategoryId.WindDirection].push(data[UnitCategoryId.WindSpeed][0])
          order = [
            UnitCategoryId.Temperature,
            UnitCategoryId.Rainfall,
            UnitCategoryId.WindDirection,
            UnitCategoryId.WindSpeed,
            UnitCategoryId.Humidity,
            UnitCategoryId.Pressure,
            UnitCategoryId.SolarRadiation,
          ]
          break

        case RicMode.WaterQuality:
          data[UnitCategoryId.WaterTemperature] = data[UnitCategoryId.Temperature]
          order = order.filter((sensorProduct) => sensorProduct !== UnitCategoryId.Temperature)
          // hardcoded
          // order = [
          //   UnitCategoryId.DissolvedOxygen,
          //   UnitCategoryId.Turbidity,
          //   UnitCategoryId.DissolvedSolids,
          //   UnitCategoryId.Nitrates,
          //   UnitCategoryId.SpecificConductivity,
          //   UnitCategoryId.Salinity,
          //   UnitCategoryId.WaterTemperature,
          // ]
          order.push(UnitCategoryId.WaterTemperature)
          break

        // case RicMode.DirtTracker:
        // case RicMode.FlowMeter:
        // case RicMode.Unknown:
        default:
          break
      }

      order.forEach((unitCategoryId) => {
        const chart: JSX.Element | null =
          data[unitCategoryId].length > 1 ||
          ['Vegetronix VH400', 'Vegetronix THERM200', 'Irrometer WM200SS'].includes(
            data[unitCategoryId][0].metadata.productName
          )
            ? renderUnifiedCharts(data[unitCategoryId])
            : renderIndividualChart(unitCategoryId, data[unitCategoryId][0])

        if (chart) {
          charts.push(chart)
        }
      })

      return charts
    }

    return charts
  }

  function renderBatteryCharts(batteryReadings: any): JSX.Element[] {
    if (batteryReadings) {
      return Object.keys(batteryReadings).map((key) => {
        return (
          <BatteryLineChart
            key={key}
            device={device}
            isLoading={isLoading}
            chartName={key === 'battery' ? 'Battery' : 'Port C Power'}
            batteryReadings={batteryReadings[key]}
            viewport={viewport}
            userUnitSettings={userUnitSettings}
            unitConversions={unitConversions}
          />
        )
      })
    }

    return [<></>]
  }

  function renderIndividualChart(unitCategoryId: number, readings: any) {
    const sensorPort = readings.metadata.sensorName
    const sensorProductName = readings.metadata.productName

    const sensorProduct =
      unitCategoryId === UnitCategoryId.WaterTemperature
        ? SensorProduct.AquaTroll500TemperatureSensor
        : Object.values(SensorProduct).find((sensorProduct) =>
            sensorProductName ? sensorProduct.includes(sensorProductName) : false
          )

    const key = `${sensorPort}-${sensorProduct?.split(' ').join('')}`

    switch (sensorProduct) {
      // case SensorProduct.IrrometerWM200SS:

      case SensorProduct.AquaTroll500DissolvedOxygenSensor:
      case SensorProduct.AquaTroll500NitrateSensor:
      case SensorProduct.AquaTroll500SalinitySensor:
      case SensorProduct.AquaTroll500SpecificConductivitySensor:
      case SensorProduct.AquaTroll500TotalDissolvedSolidsSensor:
      case SensorProduct.AquaTroll500TurbiditySensor:
      case SensorProduct.AquaTroll500TemperatureSensor:
      case SensorProduct.Battery:
      case SensorProduct.Humidity:
      case SensorProduct.Pressure:
      case SensorProduct.SolarRadiation:
      case SensorProduct.Temperature:
      case SensorProduct.WindSpeed:
        return (
          <GenericLineChart
            key={key}
            device={device}
            isLoading={isLoading}
            sensorProduct={sensorProduct}
            sensorPort={sensorPort}
            sensorReadings={readings}
            viewport={viewport}
            userUnitSettings={userUnitSettings}
            unitConversions={unitConversions}
          />
        )

      case SensorProduct.Rainfall:
      case SensorProduct.OpticalRainSensorSAB:
      case SensorProduct.OpticalRainSensorPower:
        return (
          <RainfallChart
            key={key}
            device={device}
            sensorProduct={sensorProduct}
            sensorPort={sensorPort}
            sensorReadings={readings}
            viewport={viewport}
            userUnitSettings={userUnitSettings}
            unitConversions={unitConversions}
          />
        )

      case SensorProduct.VegetronixVH400:
        return (
          <VegetronixVH400Chart
            key={key}
            device={device}
            sensorPort={sensorPort}
            sensorReadings={readings}
            viewport={viewport}
            isLoading={isLoading}
            userUnitSettings={userUnitSettings}
          />
        )

      case SensorProduct.VegetronixTHERM200:
        return (
          <VegetronixTHERM200Chart
            key={key}
            device={device}
            sensorPort={sensorPort}
            sensorReadings={readings}
            viewport={viewport}
            isLoading={isLoading}
            userUnitSettings={userUnitSettings}
          />
        )

      case SensorProduct.BadgermeterSDI:
        return (
          <BadgerSDIChart
            key={key}
            device={device}
            sigfoxId={sigfoxId}
            isLoading={isLoading}
            sensorPort={sensorPort}
            sensorProduct={sensorProduct}
            sensorReadings={[readings]}
            viewport={viewport}
            currentTime={currentTime}
            userUnitSettings={userUnitSettings}
          />
        )

      default:
        // do nothing
        break
    }
    return null
  }

  function renderUnifiedCharts(readings: any[]) {
    const sensorType = getAllSensors(readings)[0]
    const sensorPort = readings[0].metadata.sensorName

    switch (sensorType) {
      case 'Vegetronix VH400':
        return (
          <UnifiedChartVegetronixVH400
            device={device}
            sensorType={SensorProduct.VegetronixVH400}
            sensorReadings={readings}
            viewport={viewport}
            isLoading={isLoading}
          />
        )

      case 'Vegetronix THERM200':
        return (
          <UnifiedChartVegetronixTHERM200
            device={device}
            sensorType={SensorProduct.VegetronixTHERM200}
            sensorReadings={readings}
            viewport={viewport}
            isLoading={isLoading}
          />
        )

      case 'Irrometer WM200SS':
        return (
          <UnifiedChartIrrometer
            device={device}
            sensorType={SensorProduct.IrrometerWM200SS}
            sensorReadings={readings}
            viewport={viewport}
            isLoading={isLoading}
            userUnitSettings={userUnitSettings}
          />
        )

      case SensorProduct.WindDirection:
        return (
          <WindDirectionChart
            device={device}
            sensorPort={sensorPort}
            readings={readings}
            sensorReadings={readings[0]}
            chartUnits="degrees"
            viewport={viewport}
            userUnitSettings={userUnitSettings}
          />
        )

      case SensorProduct.Badgermeter220BR:
      case SensorProduct.DEMPaddleWheel:
        return (
          <FlowMeterChart
            device={device}
            sigfoxId={sigfoxId}
            isLoading={isLoading}
            sensorPort={sensorPort}
            sensorProduct={sensorType}
            sensorReadings={readings}
            viewport={viewport}
            currentTime={currentTime}
            userUnitSettings={userUnitSettings}
          />
        )
    }

    return null
  }

  function containsSensor(sensorType: SensorProduct) {
    return getAllCurrentSensors().includes(sensorType)
  }

  function getAllCurrentSensors() {
    return sensorConfigs
      ? Object.keys(sensorConfigs).map((port) => {
          const data = sensorConfigs[port] as any
          return data.sensorProductName
        })
      : []
  }

  function determineRicMode(deviceConfig: any, readings: any): RicMode {
    let mode = RicMode.Unknown

    if (deviceConfig !== undefined) {
      const deviceMode = Number(deviceConfig.deviceMode)
      mode = RicModeLookup[deviceMode] || RicMode.Unknown
    }

    if (mode === RicMode.Standard || mode === RicMode.Unknown) {
      if (isFlowMeter(readings)) {
        mode = RicMode.FlowMeter
      }
    }

    return mode
  }

  function getAllSensors(readings: any) {
    if (readings === undefined) {
      return []
    }

    return Object.values(readings)
      .map((reading: any) => reading.metadata.productName)
      .filter((sensor) => sensor !== 'Temperature Sensor')
  }

  function isFlowMeter(readings: any) {
    if (readings === undefined) {
      return false
    }

    for (const key of Object.keys(readings)) {
      const reading = readings[key]

      if (reading?.metadata?.productName?.toLowerCase().includes('badgermeter 220br')) {
        return true
      }
      if (reading?.metadata?.productName?.toLowerCase().includes('paddlewheel')) {
        return true
      }
    }

    return false
  }

  return (
    <div className={tailwind.container}>
      <>
        <DeviceDetails device={device} />
        <DeviceSiteDetails device={device} />
        {sensorConfigs && Object.keys(sensorConfigs).length > 0 ? (
          <DeviceSensorSettings sigfoxId={sigfoxId} sensorConfigs={sensorConfigs} />
        ) : null}
        {containsSensor(SensorProduct.VegetronixVH400) ? (
          <DeviceAlertSettings sigfoxId={sigfoxId} globalAlertSettings={globalAlertSettings} />
        ) : null}
      </>
      {/* <ChartControls setTimeRange={setTimeRange} /> */}
      {isLoading ? (
        <div className={tailwind.spinner}>
          <Spinner />
        </div>
      ) : null}

      <div>
        {sigfoxId && isLoading === false
          ? showBatteryCharts
            ? renderBatteryCharts(batteryReadings).map((chart, index) => {
                return (
                  <div key={index} className={tailwind.chart}>
                    {chart}
                  </div>
                )
              })
            : renderAllCharts(readings).map((chart, index) => {
                return (
                  <div key={index} className={tailwind.chart}>
                    {chart}
                  </div>
                )
              })
          : null}
      </div>
    </div>
  )
}

const tailwind = {
  container: 'mx-1',
  chart: 'mb-0',
  spinner: 'absolute top-1/3 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50',
  buttonEnabled:
    'bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-4 rounded focus:outline-none focus:shadow-outline mb-0',
  buttonDisabled: 'bg-gray-400 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline mb-3',
  alert: 'bg-red-500 text-white font-bold rounded px-4 py-2 mb-10 mt-6 h20 w-[320px] mx-auto text-center',
}
